import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Barcode,
  Plus,
  Minus,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  Truck,
  MapPin,
  Package,
} from "lucide-react";
import { API } from "@/App";

export default function ReceivingSession({ user }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const barcodeInputRef = useRef(null);

  const [session, setSession] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);

  const [addForm, setAddForm] = useState({
    product_id: "",
    barcode_search: "",
    expected_quantity: 0,
    received_quantity: 1,
  });

  useEffect(() => {
    fetchSession();
    fetchProducts();
  }, [id]);

  const fetchSession = async () => {
    try {
      const response = await fetch(`${API}/receiving-sessions/${id}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setSession(data);
      } else {
        toast.error("Session not found");
        navigate("/receiving");
      }
    } catch (error) {
      toast.error("Error fetching session");
      navigate("/receiving");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API}/products`, { credentials: "include" });
      if (response.ok) {
        setProducts(await response.json());
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleBarcodeSearch = async (barcode) => {
    if (!barcode) return;
    
    try {
      const response = await fetch(`${API}/products/barcode/${barcode}`, {
        credentials: "include",
      });
      if (response.ok) {
        const product = await response.json();
        setAddForm((prev) => ({ ...prev, product_id: product.product_id, barcode_search: "" }));
        toast.success(`Found: ${product.name}`);
      } else {
        toast.error("Product not found with this barcode");
      }
    } catch (error) {
      toast.error("Error searching barcode");
    }
  };

  const handleAddItem = async () => {
    if (!addForm.product_id || addForm.received_quantity < 1) {
      toast.error("Please select a product and enter quantity");
      return;
    }

    setAdding(true);
    try {
      const response = await fetch(`${API}/receiving-sessions/${id}/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: addForm.product_id,
          expected_quantity: addForm.expected_quantity,
          received_quantity: addForm.received_quantity,
        }),
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setSession((prev) => ({
          ...prev,
          items: data.items,
          discrepancies: data.discrepancies,
        }));
        setAddForm({ product_id: "", barcode_search: "", expected_quantity: 0, received_quantity: 1 });
        toast.success("Item added");
        barcodeInputRef.current?.focus();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add item");
      }
    } catch (error) {
      toast.error("Error adding item");
    } finally {
      setAdding(false);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const response = await fetch(`${API}/receiving-sessions/${id}/complete`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Session completed! Inventory updated.");
        navigate("/receiving");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to complete session");
      }
    } catch (error) {
      toast.error("Error completing session");
    } finally {
      setCompleting(false);
      setCompleteDialogOpen(false);
    }
  };

  const handleCancel = async () => {
    try {
      const response = await fetch(`${API}/receiving-sessions/${id}/cancel`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Session cancelled");
        navigate("/receiving");
      } else {
        toast.error("Failed to cancel session");
      }
    } catch (error) {
      toast.error("Error cancelling session");
    }
    setCancelDialogOpen(false);
  };

  const adjustQuantity = (delta) => {
    setAddForm((prev) => ({
      ...prev,
      received_quantity: Math.max(1, prev.received_quantity + delta),
    }));
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const isInProgress = session?.status === "in_progress";

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="receiving-session-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/receiving")}
            className="rounded-sm"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl font-bold tracking-tight">
                Receiving Session
              </h1>
              <Badge
                className={`rounded-sm ${
                  session?.status === "completed"
                    ? "status-completed"
                    : session?.status === "cancelled"
                    ? "status-cancelled"
                    : "status-in-progress"
                }`}
              >
                {session?.status}
              </Badge>
            </div>
            <p className="text-muted-foreground font-data text-sm">{session?.session_id}</p>
          </div>
          {isInProgress && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setCancelDialogOpen(true)}
                className="rounded-sm"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={() => setCompleteDialogOpen(true)}
                className="rounded-sm"
                data-testid="complete-session-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Complete
              </Button>
            </div>
          )}
        </div>

        {/* Session Info */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="rounded-sm border">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-primary/10 flex items-center justify-center">
                <Truck className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Supplier</p>
                <p className="font-medium">{session?.supplier?.name}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-sm border">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-primary/10 flex items-center justify-center">
                <MapPin className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Location</p>
                <p className="font-medium">{session?.location?.name}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-sm border">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-primary/10 flex items-center justify-center">
                <Package className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Items Received</p>
                <p className="font-data text-xl font-semibold">{session?.items?.length || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Add Item Form */}
        {isInProgress && (
          <Card className="rounded-sm border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Barcode className="h-5 w-5" />
                Add Item
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Barcode Search */}
                <div className="lg:col-span-1 space-y-2">
                  <Label>Scan Barcode</Label>
                  <Input
                    ref={barcodeInputRef}
                    value={addForm.barcode_search}
                    onChange={(e) => setAddForm({ ...addForm, barcode_search: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleBarcodeSearch(addForm.barcode_search);
                      }
                    }}
                    placeholder="Scan or enter barcode"
                    className="rounded-sm font-data"
                    data-testid="barcode-input"
                  />
                </div>

                {/* Product Select */}
                <div className="lg:col-span-1 space-y-2">
                  <Label>Or Select Product</Label>
                  <Select
                    value={addForm.product_id}
                    onValueChange={(v) => setAddForm({ ...addForm, product_id: v })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="product-select">
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((p) => (
                        <SelectItem key={p.product_id} value={p.product_id}>
                          {p.name} ({p.sku})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Quantity */}
                <div className="space-y-2">
                  <Label>Quantity Received</Label>
                  <div className="quantity-input">
                    <button type="button" onClick={() => adjustQuantity(-1)}>
                      <Minus className="h-4 w-4" />
                    </button>
                    <Input
                      type="number"
                      min="1"
                      value={addForm.received_quantity}
                      onChange={(e) =>
                        setAddForm({ ...addForm, received_quantity: parseInt(e.target.value) || 1 })
                      }
                      className="border-0 text-center"
                      data-testid="quantity-input"
                    />
                    <button type="button" onClick={() => adjustQuantity(1)}>
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Add Button */}
                <div className="flex items-end">
                  <Button
                    onClick={handleAddItem}
                    disabled={adding || !addForm.product_id}
                    className="rounded-sm w-full"
                    data-testid="add-item-btn"
                  >
                    {adding ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Item
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Items List */}
        <Card className="rounded-sm border">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading">Received Items</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {session?.items?.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No items received yet</p>
                <p className="text-sm">Start scanning barcodes or select products above</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="font-data">SKU</TableHead>
                      <TableHead className="font-data text-right">Received</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {session?.items?.map((item, index) => (
                      <TableRow key={index} className="data-row">
                        <TableCell className="font-medium">{item.product_name}</TableCell>
                        <TableCell className="font-data text-sm">{item.sku}</TableCell>
                        <TableCell className="font-data text-right text-lg font-semibold">
                          {item.received_quantity}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Discrepancies */}
        {session?.discrepancies?.length > 0 && (
          <Card className="rounded-sm border border-orange-500/20 bg-orange-500/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading flex items-center gap-2 text-orange-500">
                <AlertTriangle className="h-5 w-5" />
                Discrepancies
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="font-data text-right">Expected</TableHead>
                    <TableHead className="font-data text-right">Received</TableHead>
                    <TableHead className="font-data text-right">Variance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {session.discrepancies.map((disc, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{disc.product_name}</TableCell>
                      <TableCell className="font-data text-right">{disc.expected}</TableCell>
                      <TableCell className="font-data text-right">{disc.received}</TableCell>
                      <TableCell className={`font-data text-right font-semibold ${disc.variance < 0 ? "text-red-500" : "text-emerald-500"}`}>
                        {disc.variance > 0 ? "+" : ""}{disc.variance}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Cancel Dialog */}
        <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel Session</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to cancel this receiving session? Items will not be added to inventory.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Go Back</AlertDialogCancel>
              <AlertDialogAction onClick={handleCancel} className="bg-destructive text-destructive-foreground">
                Cancel Session
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Complete Dialog */}
        <AlertDialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Complete Session</AlertDialogTitle>
              <AlertDialogDescription>
                This will add all received items to your inventory. Make sure all items are scanned.
                {session?.discrepancies?.length > 0 && (
                  <span className="block mt-2 text-orange-500">
                    Note: There are {session.discrepancies.length} discrepancy(ies) logged.
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Go Back</AlertDialogCancel>
              <AlertDialogAction onClick={handleComplete} disabled={completing}>
                {completing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Complete & Update Inventory
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
